
import React, { useState } from 'react'
import { Icon } from '@/components/ui/icon'
import { cn } from '@/lib/utils'
import { LibraryModelPicker } from './components/LibraryModelPicker'
import type { LibraryModel } from '@/hooks/useLibraryModels'

interface NodeSettingsBasicSectionProps {
  libraryModels: LibraryModel[]
  libraryLoading: boolean
  libraryEmpty: boolean
  libraryError: unknown
  refetchLibrary: () => void
  selectedModelId: string
  onModelChange: (modelId: string, brandSlug: string | null) => void
}

/** Model selection section with collapsible toggle */
export const NodeSettingsBasicSection: React.FC<NodeSettingsBasicSectionProps> = ({
  libraryModels,
  libraryLoading,
  libraryEmpty,
  libraryError,
  refetchLibrary,
  selectedModelId,
  onModelChange,
}) => {
  const [collapsed, setCollapsed] = useState(false)

  return (
    <div className="px-3 py-2 space-y-4">
      <button
        type="button"
        aria-expanded={!collapsed}
        onClick={() => setCollapsed(!collapsed)}
        className="group flex w-full items-center justify-between transition-colors hover:text-foreground"
      >
        <span className="text-sm font-semibold">Basic Settings</span>
        <Icon
          name="chevron-down"
          className={cn(
            "h-4 w-4 text-muted-foreground transition-transform",
            collapsed && "rotate-180"
          )}
        />
      </button>

      {!collapsed && (
        <>
          {/* Library Error */}
          {libraryError && (
            <div role="alert" className="p-3 text-sm text-destructive flex items-center justify-between gap-3 rounded-md bg-destructive/10">
              <span>Failed to load models</span>
              <button type="button" onClick={refetchLibrary} className="shrink-0 text-xs underline hover:text-destructive/80">Retry</button>
            </div>
          )}

          {/* Empty state */}
          {libraryEmpty && !libraryError && (
            <button
              onClick={() => document.dispatchEvent(new CustomEvent('genie:open-library'))}
              className="flex w-full flex-col items-center gap-2 rounded-md bg-muted/50 p-4 text-center cursor-pointer hover:bg-muted/70 transition-colors"
            >
              <Icon name="plus-circle" className="h-6 w-6 text-muted-foreground/60" />
              <span className="text-sm text-muted-foreground">No models available for this node</span>
              <span className="text-xs text-primary hover:underline">Browse Library</span>
            </button>
          )}

          {/* Model Selector */}
          {!libraryError && <div className="flex min-w-0 items-center justify-between gap-3">
            <div className="flex items-center gap-2">
              <Icon name="cpu" className="h-4 w-4 text-muted-foreground" />
              <span className="text-sm">Model</span>
            </div>
            <LibraryModelPicker
              models={libraryModels}
              value={selectedModelId}
              onValueChange={onModelChange}
              isLoading={libraryLoading}
              isEmpty={libraryEmpty}
            />
          </div>}
        </>
      )}
    </div>
  )
}
